import { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Footer from "../../components/Footer";
import Loader from "../../components/Loader/Loader";
import PdfCanvasPreview from "../../components/PdfCanvasPreview";
import { documentSideConfigurationConstants } from "../../constants/documentSideConfigConstants.js";
import { languageData } from "../../constants/languageData";
import GlobalConfig from "../../context/GlobalConfig";
import JourneyConfig from "../../context/JourneyConfig";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { convertImageToUrl } from "../../utils/base64ToUrlUtility";
import { getDocumentFileTypes } from "../../constants/fileTypeConstants";
import { getDocumentCountry, getDocumentIdType } from "../../utils/documentFields";
import Head from "../Head";

/**
 * Hand control to the generic ErrorHandler screen ("Oops! Something
 * went wrong."). Used when update-journey itself fails; upload failures
 * fall back to a base64 payload instead of landing here.
 */
function routeToErrorScreen(stepperFunc, context = "upload") {
  console.error(`[ImageCheck] Routing to error screen | context=${context}`);
  sessionStorage.setItem("error", "true");
  // Intentionally do NOT set errorCategory — ErrorHandler then renders
  // the GenericError variant with the default "Oops! Something went
  // wrong" texts from languageData.
  sessionStorage.removeItem("errorCategory");
  sessionStorage.removeItem("errorReason");
  const currentStepFromStorage = sessionStorage.getItem("currentStep");
  stepperFunc(currentStepFromStorage ? parseInt(currentStepFromStorage) : 0);
}

const ImageCheck = ({
  image,
  stepperFunc,
  triggeredFrom,
  steps,
  isFirstDocumentVerificationDone,
  currentDocumentStep,
  currentStep,
  stepObj,
  currentDocumentRetries,
  capturingMode,
  autoCaptureLoadingTime,
}) => {
  const { config } = useContext(GlobalConfig);
  const { journey, updateJourney, getJourney } = useContext(JourneyConfig);
  // This document's own type, not the single-slot `sessionStorage['document']` channel (IDX-677):
  // that global key holds one type for the whole journey and is rewritten from a two-document
  // ternary on every load, so on a 3-document journey it would skip document 3's back capture
  // using document 2's type. getDocumentIdType reads the per-document `idType{N}` from the journey.
  const documentType = getDocumentIdType(journey, currentDocumentStep);
  const documentFailureReason = sessionStorage.getItem('documentFailureReason');
  const [sameSideDocumentFlagNumber, setSameSideDocumentFlagNumber] = useState(
    documentFailureReason
      ? documentFailureReason === "ONLY_FRONT"
        ? 1
        : documentFailureReason === "ONLY_BACK"
          ? 2
          : -1
      : -1
  );
  const documentSideConfiguration = config?.documentCapture?.[currentDocumentStep]?.documentSideConfiguration ?? documentSideConfigurationConstants.FRONT_MANDATORY_BACK_OPTIONAL;
  const nexStepToBe = steps?.[0];
  const nextStep = triggeredFrom === "front" ? ((documentType?.includes('Passport') || documentType?.includes("PAN")) || documentSideConfiguration === documentSideConfigurationConstants.FRONT || sameSideDocumentFlagNumber!=-1) ? (steps?.[0]+2) : steps?.[0] : nexStepToBe;
  const { branding } = config ?? {};
  const [isLoading, setIsLoading] = useState(false);
  const language = sessionStorage.getItem("language") ?? "en";
  const { journeyId } = useParams();
  const [imageUrl, setImageUrl] = useState(null); // For displaying base64 image
  const isPdf =
    typeof imageUrl === "string" &&
    (imageUrl.startsWith("data:application/pdf") || imageUrl.startsWith("data:application/x-pdf"));
  const isMobile = useDeviceDetection();
  const color = config?.branding?.accentColor;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";

  useEffect(() => {
    if (!image) {
      console.log('[ImageCheck] No image provided');
      return;
    }
  
    // Ensure we have a valid base64 image string
    if (typeof image !== 'string') {
      console.error('[ImageCheck] Invalid image - not a base64 string:', {
        type: typeof image,
        value: image
      });
      return;
    }
  
    console.log('[ImageCheck] Setting base64 image');
    setImageUrl(image);
  }, [image]);

  useEffect(() => {
    if (!image) {
      stepperFunc(parseInt(currentStep) - 1);
    }
  }, []);

  // This document's own country and type, not the first document's (IDX-677). Mirrors the
  // capture screens so the value persisted at DOC_FRONT matches what was shown and captured.
  const [currentPageState, setCurrentPageState] = useState({
    country: getDocumentCountry(journey, currentDocumentStep),
    idType: getDocumentIdType(journey, currentDocumentStep),
  });

  const saveDataAndMove = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setIsLoading(true);

    try {
      console.log('[ImageCheck] Starting data save process for:', triggeredFrom);
      const { front: frontFileType, back: backFileType } = getDocumentFileTypes(currentDocumentStep);
      const fileType = triggeredFrom === "back" ? backFileType : frontFileType;

      // `convertImageToUrl` already retried the upload 3 times
      // internally (fresh presigned URL on each attempt). If `error`
      // is non-null here, those retries are exhausted.
      const { s3Key, error } = await convertImageToUrl(image, journeyId, fileType);

      // Rate-limit exhaustion → backend has terminated the journey.
      // Surface the existing isJourneyDone signal so Settings.jsx
      // routes to the VerificationTerminated screen — same UX as
      // doc-check retries exceeded.
      if (error && error.terminateJourney) {
        try {
          await updateJourney(triggeredFrom === "back" ? "DOC_BACK" : "DOC_FRONT", {}, currentStep);
        } catch (signalErr) {
          console.error("[ImageCheck] Failed to surface terminated state", signalErr);
        }
        setIsLoading(false);
        return;
      }

      // Any other upload error → fall back to embedding the raw base64
      // in update-journey (s3Key is "" here, so the frontDocBase64 /
      // backDocBase64 spreads below engage) instead of dead-ending on
      // the error screen.
      if (error) {
        console.warn(`[ImageCheck] Upload failed (${error.code}) — falling back to base64 payload`);
      }

      const persistImageUrl = s3Key;

      if (triggeredFrom === "back") {
        await updateJourney(
          "DOC_BACK",
          {
            backDoc: persistImageUrl,
            captureMode: capturingMode,
            autoCaptureLoadingTime: autoCaptureLoadingTime,
            currentDocumentStep: currentDocumentStep + 1,
            currentDocumentRetries: currentDocumentRetries,
            ...(persistImageUrl === "" && { backDocBase64: image }),
          },
          currentStep
        );
      } else if (triggeredFrom === "front") {
        if (currentPageState?.idType && currentPageState?.country) {
          sessionStorage.setItem("selectedCountry", currentPageState?.country);
        }

        await updateJourney(
          "DOC_FRONT",
          currentPageState?.idType && currentPageState?.country
            ? {
                frontDoc: persistImageUrl,
                captureMode: capturingMode,
                autoCaptureLoadingTime: autoCaptureLoadingTime,
                currentDocumentStep: currentDocumentStep + 1,
                currentDocumentRetries: currentDocumentRetries,
                sameSideDocumentFlagNumber: sameSideDocumentFlagNumber,
                ...(persistImageUrl === "" && { frontDocBase64: image }),
                ...currentPageState,
              }
            : {
                frontDoc: persistImageUrl,
                captureMode: capturingMode,
                autoCaptureLoadingTime: autoCaptureLoadingTime,
                currentDocumentStep: currentDocumentStep + 1,
                currentDocumentRetries: currentDocumentRetries,
                sameSideDocumentFlagNumber: sameSideDocumentFlagNumber,
                ...(persistImageUrl === "" && { frontDocBase64: image }),
              },
          currentStep
        );
      }

      console.log('[ImageCheck] Data save completed, moving to next step');
      stepperFunc(nextStep);
    } catch (error) {
      console.error('[ImageCheck] Error during data save:', {
        message: error?.message || 'Unknown error',
        name: error?.name || 'Unknown',
        triggeredFrom,
        currentStep,
        currentDocumentStep,
      });
      setIsLoading(false);
      routeToErrorScreen(stepperFunc, "updateJourney");
    } finally {
      setIsLoading(false);
    }
  }, [
    triggeredFrom,
    currentDocumentStep,
    currentDocumentRetries,
    capturingMode,
    autoCaptureLoadingTime,
    image,
    journeyId,
    journey,
    sameSideDocumentFlagNumber,
    currentPageState,
    currentStep,
    nextStep,
    stepperFunc,
    updateJourney,
  ]);

  useEffect(() => {
    if (journeyId && !journey) {
      getJourney(journeyId);
    }
  }, [journeyId, journey, getJourney]);


  if (isLoading) return <Loader />;
  return (
    <>
      <Head
        showBackIcon
        showLanguages={false}
        handleBack={() => stepperFunc(steps[2])}
        onNextStep={stepperFunc}
        stepperFunc={stepperFunc}
        style={{ zIndex: "100" }}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            {/* Displaying base64 image */}
            {imageUrl && !isPdf && 
              <img
                src={imageUrl}
                alt="preview"
                onError={(e) => {
                  console.error('[ImageCheck] Image failed to load:', {
                    message: e?.message || 'Unknown error',
                    type: e?.type || 'Unknown',
                    target: e?.target ? {
                      tagName: e.target.tagName,
                      src: e.target.src?.substring(0, 100) + '...'
                    } : null
                  });
                }}
                onLoad={() => {
                  console.log('[ImageCheck] Image loaded successfully');
                }}
              />
            }
            {imageUrl && isPdf && (
              <div className="flex flex-col w-full h-full min-h-[400px]" style={{ gap: 8, overflow: "visible" }}>
                {/* Use react-pdf for all devices with base64 data */}
                <PdfCanvasPreview
                  file={imageUrl}
                  openPdfLabel={languageData[language]?.pdfOpenLabel ?? "Open PDF"}
                  previewHint={languageData[language]?.pdfPreviewHint ?? "If preview doesn't appear, you can still continue."}
                  onError={(e) => console.error("[ImageCheck] react-pdf preview failed:", e)}
                />
              </div>
            )}
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div className="flex flex-col">
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {languageData[language]?.imageCheck?.heading}
              </div>
              <div className="mt-4 md:mt-3 text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left md:mb-0  mb-3">
                {languageData[language]?.imageCheck?.subHeading}
              </div>
            </div>
            <div className="flex flex-col justify-center items-center font-sans w-full">
              <div className="flex md:flex-col md:gap-[0px] gap-4 justify-center w-full">
                <div className="md:mb-4 mb-2 text-[16px] md:mt-6 md:mt-0 w-full">
                  <button
                    className=" py-3 px-4"
                    onClick={() => {
                      let goTo = 4;
                      const reversed = Object.keys(stepObj)
                        .map(Number)
                        .filter((key) => key < Number(currentStep))
                        .reverse();
                      if (triggeredFrom === "front") {
                        goTo = reversed.find((key) =>
                          stepObj?.[key].includes("front")
                        );
                      } else {
                        goTo = reversed.find((key) =>
                          stepObj?.[key].includes("back")
                        );
                      }

                      stepperFunc(goTo);
                    }}
                    style={{
                      color, // Dynamic text color
                      textDecoration: "none", // Remove underline
                      background: "none", // Remove button background
                      border: `1px solid ${color}`, // Remove border
                      borderRadius: 4,
                      cursor: "pointer", // Pointer cursor to indicate clickability
                      fontSize: "inherit", // Inherit font size from parent
                      fontWeight: "inherit", // Inherit font weight
                      textAlign: "inherit", // Inherit text alignment
                      display: "flex", // Ensure inline behavior
                      width:'100%',
                      alignItems:'center', 
                      justifyContent:'center'
                    }}
                  >
                    {languageData[language]?.imageCheck?.button1 ||
                      "Next Step"}
                  </button>
                </div>
                <button
                  className="flex flex-row items-center justify-center gap-4 w-full text-white py-3 px-4 rounded text-[16px]"
                  style={{ backgroundColor: color, height: 'fit-content' }}
                  onClick={(e) => saveDataAndMove(e)}
                >
                  {languageData[language]?.imageCheck?.button2}
                </button>
              </div>
              <Footer style={{ marginTop: "1rem" }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageCheck;
